import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, LogIn, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InfluencerLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/influencers/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Store token in sessionStorage (memory would be lost on page refresh)
        sessionStorage.setItem('_it', data.token);
        sessionStorage.setItem('influencer_data', JSON.stringify(data.influencer));
        toast.success(`¡Bienvenido/a ${data.influencer.name}!`);
        navigate('/influencers/dashboard');
      } else {
        toast.error(apiErrorMessage(data, 'Credenciales incorrectas'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neon-darker flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-cyan/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-pink/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-neon-cyan to-neon-pink rounded-2xl mb-4">
            <TrendingUp size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Portal Influencers</h1>
          <p className="text-white/60">Accede a tu dashboard de comisiones</p>
        </div>

        {/* Login Form */}
        <div className="bg-neon-dark/50 backdrop-blur-sm border border-neon-cyan/20 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm text-white/70 mb-2">Email</label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neon-cyan/50" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="w-full pl-12 pr-4 py-3 bg-neon-darker border border-neon-cyan/30 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-neon-cyan"
                  required
                  data-testid="influencer-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-2">Contraseña</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neon-cyan/50" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-4 py-3 bg-neon-darker border border-neon-cyan/30 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-neon-cyan"
                  required
                  data-testid="influencer-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-neon-cyan to-neon-pink text-white font-bold rounded-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="influencer-login-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn size={18} />
                  Iniciar Sesión
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-white/40 text-sm mt-6">
          ¿No tienes cuenta? Contacta al administrador
        </p>
      </div>
    </div>
  );
};

export default InfluencerLogin;
